import { binarySearch, compareWithEpsilon, toObject } from './genericUtils';
import InterpolatingUtil from 'utils/activity/InterpolatingUtil';
import { createIndexData } from './indexData';
import { interpolateTimestamp, interpolateTimestampFromSumDuration } from './multiSportInterpolateUtils';

/** @typedef {import('../types/dto/ActivityMetricsDTO').ActivityMetricsDTO} ActivityMetricsDTO */
/** @typedef {import('../types/dto/ActivityMetricsDTO').ActivityMetrics_MetricsByIndex} ActivityMetrics_MetricsByIndex */
/** @typedef {import('../types/dto/GeoPointDTO').GeoPointDTO} GeoPointDTO */

/**
 * @param {ActivityMetrics_MetricsByIndex[]} metricsByIndex
 * @param {*} index
 */
const getMetricsForMetricsIndex = (metricsByIndex, index) => metricsByIndex.map(({ metrics }) => metrics[index]);

/**
 * Aggregates metrics into a dictionary with the following format:
 * {
 *   sumDistance: [0..metricsCount]
 *   sumDuration: [0..metricsCount]
 * }
 *
 * @param {ActivityMetricsDTO} activityMetrics
 * @returns {{ [metricsKey: string]: number[]}}
 */
export function aggregateMetrics(activityMetrics) {
    if (!activityMetrics) return null;
    const { metricDescriptors, activityDetailMetrics } = activityMetrics;

    if (!metricDescriptors) return null;
    if (!activityDetailMetrics || activityDetailMetrics.length === 0) return null;

    /** @type {{[key: string]: number[]}} */
    const metricsByKey = metricDescriptors
        .map((metricDesc) => {
            const { metricsIndex, key } = metricDesc;
            const metricsArray = getMetricsForMetricsIndex(activityDetailMetrics, metricsIndex);
            /** @type {[string, number[]]} */
            const res = [key, metricsArray];
            return res;
        })
        .reduce(toObject, {});

    return metricsByKey;
}

/**
 * @param {number} timestamp
 * @param {GeoPointDTO} geoPoint
 * @returns
 */
export const compareTimestampToGeoPointTimestamp = (timestamp, geoPoint) =>
    compareWithEpsilon(timestamp, geoPoint.time);

/**
 * @template T
 * @param {T[]} array
 * @param {number} item
 * @param {(a: number, b: T) => number} comparator
 * @returns {{ index: number; isExactMatch: boolean }}
 */
const findWithBinarySearch = (array, item, comparator) => {
    const res = binarySearch(array, item, comparator);
    const isExactMatch = res >= 0;
    const index = isExactMatch ? res : ~res;
    return { index, isExactMatch };
};

/**
 * @param {number[]} directTimestamp ordered ascending
 * @param {number} timestamp the timestamp to search for
 */
export const findInDirectTimestamp = (directTimestamp, timestamp) =>
    findWithBinarySearch(directTimestamp, timestamp, compareWithEpsilon);

/**
 * @param {GeoPointDTO[]} polyline ordered ascending on geopointdto.time
 * @param {number} timestamp the timestamp to search for
 */
export const findInPolyline = (polyline, timestamp) =>
    findWithBinarySearch(polyline, timestamp, compareTimestampToGeoPointTimestamp);

/**
 * @param {number[]} sumDuration
 * @param {number} durationSeconds
 */
export const findInSumDuration = (sumDuration, durationSeconds) =>
    findWithBinarySearch(sumDuration, durationSeconds, compareWithEpsilon);

/**
 * @param {number[]} sumDistance
 * @param {number} distanceMeters
 * @returns
 */
export const findInSumDistance = (sumDistance, distanceMeters) =>
    findWithBinarySearch(sumDistance, distanceMeters, compareWithEpsilon);

/**
 * Gets the IndexData corresponding to the given duration
 * Interpolates IndexData.timestamp if necessary
 * @param {number} absoluteDurationSeconds
 * @param {{ sumDuration: number[]; directTimestamp: number[]}} param1
 */
export const getIndexDataForDuration = (absoluteDurationSeconds, { sumDuration, directTimestamp }) => {
    const { index, isExactMatch } = findInSumDuration(sumDuration, absoluteDurationSeconds);
    const timestamp = isExactMatch
        ? directTimestamp[index]
        : interpolateTimestampFromSumDuration(absoluteDurationSeconds, directTimestamp, sumDuration, index);

    return createIndexData(index, timestamp);
};

/**
 * Gets the IndexData corresponding to the given distance
 * Interpolates IndexData.timestamp if necessary
 * @param {number} distanceMeters
 * @param {{ sumDistance: number[]; directTimestamp: number[]}} param1
 */
export const getIndexDataForDistance = (distanceMeters, { sumDistance, directTimestamp }) => {
    const { index, isExactMatch } = findInSumDistance(sumDistance, distanceMeters);
    const timestamp = isExactMatch
        ? directTimestamp[index]
        : // if not an exact Distance match, need to interpolate the timestamp
          // metricsIndex := the place where the searched -and not found- value <absoluteDistanceSetByUser> should be inserted in the sorted array
          // which means => sumDistance[metricsIndex-1] < absoluteDistanceSetByUser < sumDistance[metricsIndex]
          // so we should get back a timestamp where: directTimestamp[metricsIndex-1] < timestamp < directTimestampe[metricsIndex]
          interpolateTimestamp(distanceMeters, directTimestamp, sumDistance, index);

    return createIndexData(index, timestamp);
};
